import { IconButton, Stack, styled, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidV4 } from 'uuid';

import { Norman } from '@/src/modules/knowledge-base/ContentSource/constants';
import { HighlightThumbDownIcon, ThumbDownIcon } from '@/src/shared/assets/ThumbDownIcon';
import { HighlightThumbUpIcon, ThumbUpIcon } from '@/src/shared/assets/ThumbUpIcon';
import { useBoolean } from '@/src/shared/hooks';
import { setFeedback } from '@/src/shared/services/apiServices/chatApis';
import type {
    AssistantMessage,
    AssistantMessageFeedback,
    ChatFlowCard,
    FeedbackFormCardProps,
} from '@/src/shared/types';
import { CardType } from '@/src/shared/types';
import { chatActions, selectCurrentConversation, selectIsChatLoading } from '@/src/store/apps/chat';
import { convertMarkdownToHtml, handleNormClick } from '@/src/utils/commonUtil';
import { getCurrentISODate } from '@/src/utils/dateHelper';

import { CopyMessageButton } from '../CopyMessageButton';
import { InformativeMessagePanel, LatestInformativeMessageItem } from '../InformativeMessagePanel';
import { AssistantLikeCardLayout } from './AssistantLikeCardLayout';

const FeedbackStack = styled(Stack)({
    flexDirection: 'row',
});

const Feedback = ({
    activityId,
    feedback,
}: {
    activityId: string;
    feedback?: AssistantMessageFeedback;
}) => {
    const dispatch = useDispatch();
    const isChatLoading = useSelector(selectIsChatLoading);
    const { conversationId } = useSelector(selectCurrentConversation);
    const disabled = useMemo(() => isChatLoading || !!feedback, [isChatLoading, feedback]);
    const onLikeClick = useCallback(async () => {
        const updatedFeedback = await setFeedback({
            conversationId,
            activityId,
            feedback: {
                like: true,
                reason: '',
                comment: '',
                feedbackTimestamp: getCurrentISODate(),
            },
        });
        dispatch(
            chatActions.updateFeedbackForMessage({
                activityId,
                feedback: updatedFeedback,
            })
        );
    }, [activityId, conversationId, dispatch]);
    const onDislikeClick = useCallback(() => {
        const tempCardActivityId = uuidV4();
        const removeTempCard = () => {
            dispatch(
                chatActions.removeCardForMessage({ activityId, cardActivityId: tempCardActivityId })
            );
        };
        const feedbackTimestamp = getCurrentISODate();
        const onSubmit = async (reason: string, comment: string) => {
            const updatedFeedback = await setFeedback({
                conversationId,
                activityId,
                feedback: {
                    like: false,
                    reason,
                    comment,
                    feedbackTimestamp,
                },
            });
            removeTempCard();
            dispatch(
                chatActions.updateFeedbackForMessage({
                    activityId,
                    feedback: updatedFeedback,
                })
            );
        };
        const card: ChatFlowCard<FeedbackFormCardProps> = {
            activityId: tempCardActivityId,
            cardType: CardType.FeedbackFormCard,
            cardProps: {
                timestamp: feedbackTimestamp,
                onSubmit,
                onCancel: removeTempCard,
            },
        };
        dispatch(chatActions.addCardForMessage({ activityId, card }));
    }, [activityId, conversationId, dispatch]);

    return (
        <FeedbackStack>
            <IconButton onClick={onLikeClick} disabled={disabled}>
                {feedback && feedback.like ? <HighlightThumbUpIcon /> : <ThumbUpIcon />}
            </IconButton>
            <IconButton onClick={onDislikeClick} disabled={disabled}>
                {feedback && !feedback.like ? <HighlightThumbDownIcon /> : <ThumbDownIcon />}
            </IconButton>
        </FeedbackStack>
    );
};

const Header = ({
    message,
    isInformativePanelOpen,
    onToggleInformativePanelOpen,
}: {
    message: AssistantMessage;
    isInformativePanelOpen: boolean;
    onToggleInformativePanelOpen: () => void;
}) => {
    const { informativeMessageMap } = message;
    const latestInformativeMessage = useMemo(
        () => Object.values(informativeMessageMap).at(-1),
        [informativeMessageMap]
    );
    return (
        <Typography variant="body1">
            {latestInformativeMessage && (
                <LatestInformativeMessageItem
                    informativeMessage={latestInformativeMessage}
                    isInformativePanelOpen={isInformativePanelOpen}
                    onToggleInformativePanelOpen={onToggleInformativePanelOpen}
                />
            )}
        </Typography>
    );
};

const AssistantMessageActionsStack = styled(Stack)({
    flexDirection: 'row',
});

export const AssistantMessageCard = ({ message }: { message: AssistantMessage }) => {
    const {
        content,
        activityId,
        timestamp,
        feedback,
        informativeMessageMap,
        isStreaming,
        isAuthorize,
    } = message;
    const [
        isInformativePanelOpen,
        { toggle: toggleInformativePanelOpen, set: setIsInformativePanelOpen },
    ] = useBoolean(false);
    useEffect(() => {
        setIsInformativePanelOpen(!!isStreaming);
    }, [isStreaming]);

    const shouldRenderActionButtons = isAuthorize !== false && !isStreaming;

    const handleLinkClick = async (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const anchor = target.closest('a') as HTMLAnchorElement | null;

        if (!anchor) return;

        const url = anchor.href;

        // Only handle Norman URLs
        if (!url.toLowerCase().includes(Norman)) {
            return;
        }

        e.preventDefault();

        await handleNormClick(url);
    };

    return (
        <AssistantLikeCardLayout timestamp={timestamp}>
            <Header
                message={message}
                isInformativePanelOpen={isInformativePanelOpen}
                onToggleInformativePanelOpen={toggleInformativePanelOpen}
            />
            {isInformativePanelOpen && (
                <InformativeMessagePanel informativeMessageMap={informativeMessageMap} />
            )}
            <div
                className="md-content"
                onClick={handleLinkClick}
                dangerouslySetInnerHTML={{
                    __html: convertMarkdownToHtml(content),
                }}
            ></div>
            {shouldRenderActionButtons && (
                <AssistantMessageActionsStack>
                    <Feedback activityId={activityId} feedback={feedback} />
                    <CopyMessageButton message={content} html={convertMarkdownToHtml(content)} />
                </AssistantMessageActionsStack>
            )}
        </AssistantLikeCardLayout>
    );
};
